/**
 * CLAUDE API SERVICE - Phase 2/3
 * Natural Language Understanding using Claude LLM
 *
 * Sends queries to Claude API for intelligent interpretation
 * Returns structured JSON for filtering, prioritization, etc.
 */

// Claude API Configuration
// Maximum tokens for Claude response - enough for structured JSON with reasoning (~500 chars)
const CLAUDE_MAX_TOKENS = 1000;

export interface ClaudeQueryRequest {
  query: string;
  context?: {
    previousQuery?: string;
    currentResults?: Array<{
      invoiceNumber: string;
      customerName: string;
      amount: number;
      daysOverdue?: number;
    }>;
    previousResults?: any[];
    resultCount?: number;
    totalAmount?: number;
  };
}

export interface ClaudeQueryResponse {
  action:
    | "new_query"
    | "filter_current_results"
    | "prioritize"
    | "calculate"
    | "expand"
    | "reset";
  dataType: "invoices";
  filters?: {
    status?: string;
    customerName?: string;
    minAmount?: number;
    maxAmount?: number;
    minDaysOverdue?: number;
  };
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  userIntent: string;
  confidence: "high" | "medium" | "low";
  isFollowUp?: boolean;
  followUpType?:
    | "prioritization"
    | "filtering"
    | "calculation"
    | "expansion"
    | "reset";
  prioritization?: {
    criteria: string[];
    reasoning: string;
  };
}

/**
 * Call Claude API to interpret user query
 */
export async function interpretQueryWithClaude(
  request: ClaudeQueryRequest,
): Promise<ClaudeQueryResponse> {
  console.log("[claudeApiService] 🤖 Calling Claude API...");
  console.log("[claudeApiService] Query:", request.query);
  console.log("[claudeApiService] Context:", request.context ? "Yes" : "No");

  // Build context summary for Claude
  let contextSummary = "";
  if (
    request.context?.currentResults &&
    request.context.currentResults.length > 0
  ) {
    contextSummary = `\n\nCONVERSATION CONTEXT:
Previous query: "${request.context.previousQuery || "N/A"}"
Current results displayed: ${request.context.resultCount || 0} invoices
Total amount: £${request.context.totalAmount?.toLocaleString() || 0}

Current invoices shown:
${request.context.currentResults
  .map(
    (inv, idx) =>
      `${idx + 1}. ${inv.invoiceNumber} - ${inv.customerName} - £${inv.amount.toLocaleString()}${inv.daysOverdue ? ` (${inv.daysOverdue} days overdue)` : ""}`,
  )
  .join("\n")}

The user is now asking a FOLLOW-UP question about these results.`;
  }

  const prompt = `You are a cash flow forecasting assistant. Analyse this user query and return filtering/action instructions.

User query: "${request.query}"${contextSummary}

Available invoice data fields:
- invoiceNumber (e.g., "INV-1234")
- customerName (e.g., "BigCo Ltd", "Education Facilities Ltd")
- amount (number, in GBP)
- status ("paid", "unpaid", "overdue")
- daysOverdue (number, only for overdue invoices)

IMPORTANT RULES:
1. If there is CONVERSATION CONTEXT above, this is a FOLLOW-UP question
   - action should be "filter_current_results" (filter the current results shown)
   - Extract clean customer names (e.g., "BigCo" should match "BigCo Ltd")
   
2. If NO context, this is a NEW query
   - action should be "new_query"
   
3. For prioritization questions ("which should I chase first?", "what's most urgent?"):
   - action should be "prioritise"
   - followUpType should be "prioritization"
   
4. Extract CLEAN values:
   - Customer names: Extract just the name (e.g., from "Just show me BigCo" extract "BigCo")
   - Amounts: Handle "k" suffix (£20k = 20000, £2m = 2000000)
   
5. Be case-insensitive for matching

Respond with ONLY valid JSON (no markdown, no explanation):
{
  "action": "new_query" | "filter_current_results" | "prioritize" | "calculate" | "expand" | "reset",
  "dataType": "invoices",
  "filters": {
    "status": "overdue" | "unpaid" | "paid",
    "customerName": "extracted customer name",
    "minAmount": number,
    "maxAmount": number,
    "minDaysOverdue": number
  },
  "sortBy": "amount" | "daysOverdue" | "customerName",
  "sortOrder": "asc" | "desc",
  "userIntent": "human readable description",
  "confidence": "high" | "medium" | "low",
  "isFollowUp": boolean,
  "followUpType": "prioritization" | "filtering" | "calculation" | "expansion" | "reset",
  "prioritization": {
    "criteria": ["amount", "daysOverdue"],
    "reasoning": "explanation"
  }
}`;

  try {
    const response = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": "placeholder", // In Evo Builder, this should be handled by environment
        "anthropic-version": "2023-06-01",
      },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: CLAUDE_MAX_TOKENS,
        messages: [
          {
            role: "user",
            content: prompt,
          },
        ],
      }),
    });

    if (!response.ok) {
      console.error(
        "[claudeApiService] ❌ API call failed:",
        response.status,
        response.statusText,
      );
      throw new Error(`Claude API error: ${response.status}`);
    }

    const data = await response.json();
    console.log("[claudeApiService] ✅ Claude response received");

    // Parse Claude's response
    const textContent = data.content[0].text;
    console.log("[claudeApiService] Raw response:", textContent);

    // Remove markdown code blocks if present
    const jsonText = textContent
      .replace(/```json\n?/g, "")
      .replace(/```\n?/g, "")
      .trim();
    const interpretation: ClaudeQueryResponse = JSON.parse(jsonText);

    console.log("[claudeApiService] ✅ Parsed interpretation:", interpretation);
    return interpretation;
  } catch (error) {
    console.error("[claudeApiService] ❌ Error calling Claude API:", error);

    // Fallback to pattern-based interpretation if Claude fails
    console.log(
      "[claudeApiService] ⚠️ Falling back to pattern-based interpretation",
    );
    return fallbackInterpretation(request);
  }
}

/**
 * Fallback pattern-based interpretation if Claude API fails
 */
function fallbackInterpretation(
  request: ClaudeQueryRequest,
): ClaudeQueryResponse {
  const query = request.query.toLowerCase().trim();

  // Check if it's a follow-up with context
  const isFollowUp =
    !!request.context?.currentResults &&
    request.context.currentResults.length > 0;

  // Prioritization
  if (
    query.includes("chase first") ||
    query.includes("most urgent") ||
    query.includes("priorit")
  ) {
    return {
      action: "prioritize",
      dataType: "invoices",
      userIntent: "Prioritise current invoices",
      confidence: "high",
      isFollowUp: true,
      followUpType: "prioritization",
      prioritization: {
        criteria: ["amount", "daysOverdue"],
        reasoning: "Prioritise by highest amount and longest overdue",
      },
    };
  }

  // Customer filtering (follow-up)
  if (isFollowUp) {
    const customerMatch =
      query.match(
        /(?:just|only)\s+(?:show\s+(?:me\s+)?)?([a-z0-9\s]+?)(?:\s+only)?$/i,
      ) ||
      query.match(
        /(?:show|filter)\s+(?:me\s+)?(?:just\s+)?([a-z0-9\s]+?)(?:\s+only)?$/i,
      );
    if (customerMatch) {
      const customerName = customerMatch[1].trim();
      return {
        action: "filter_current_results",
        dataType: "invoices",
        filters: {
          customerName,
        },
        sortBy: "amount",
        sortOrder: "desc",
        userIntent: `Filter to ${customerName} only`,
        confidence: "high",
        isFollowUp: true,
        followUpType: "filtering",
      };
    }
  }

  // Overdue invoices (new query)
  if (
    query.includes("overdue") ||
    query.includes("late") ||
    query.includes("outstanding") ||
    query.includes("unpaid") ||
    query.includes("owe")
  ) {
    return {
      action: "new_query",
      dataType: "invoices",
      filters: {
        status: "overdue",
      },
      sortBy: "amount",
      sortOrder: "desc",
      userIntent: "Show overdue invoices",
      confidence: "high",
    };
  }

  // Default: treat as new overdue query
  return {
    action: "new_query",
    dataType: "invoices",
    filters: {
      status: "overdue",
    },
    sortBy: "amount",
    sortOrder: "desc",
    userIntent: "Show overdue invoices",
    confidence: "medium",
  };
}
